// QaCoachButtons — per-engineer "Coach" + "Encourage" actions that ask Claude
// (via /api/pulse/coach) for a grounded coaching recommendation or encouragement
// note. Ported from qa-live (QA-2286); the Flowbite Modal is replaced with a
// lightweight inline overlay since this app doesn't depend on Flowbite.

import { useState } from 'react'
import { focusMix } from './focusMix.js'
import { PULSE_COLORS } from './pulseConfig.js'

const MODE_META = {
    recommendation: {
        label: 'Coach',
        emoji: '💡',
        title: 'Coaching Recommendation',
        blurb: 'Claude analyzes this engineer against the team to suggest where to grow next.',
    },
    encouragement: {
        label: 'Encourage',
        emoji: '💌',
        title: 'Encouragement Note',
        blurb: 'A genuine, shareable note recognizing what this engineer is doing well.',
    },
}

function Spinner() {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            style={{ animation: 'qaCoachSpin 0.8s linear infinite' }}
            aria-hidden="true"
        >
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity: 0.25 }} />
            <path fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" style={{ opacity: 0.75 }} />
        </svg>
    )
}

// Render Claude's lightweight markdown (**bold** + line breaks) without a dependency.
function renderText(text) {
    return text.split('\n').map((line, i) => {
        if (!line.trim()) return <div key={i} style={{ height: '8px' }} />
        const parts = line.split(/(\*\*[^*]+\*\*)/g)
        return (
            <p key={i} style={{ fontSize: '13px', lineHeight: '24px', color: PULSE_COLORS.text, margin: 0 }}>
                {parts.map((p, j) =>
                    p.startsWith('**') && p.endsWith('**') ? (
                        <strong key={j} style={{ fontWeight: 600, color: PULSE_COLORS.text }}>
                            {p.slice(2, -2)}
                        </strong>
                    ) : (
                        <span key={j}>{p}</span>
                    )
                )}
            </p>
        )
    })
}

const pillButton = (accent, bg, border) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    borderRadius: '6px',
    border: `1px solid ${border}`,
    background: bg,
    padding: '2px 6px',
    fontSize: '11px',
    fontWeight: 600,
    color: accent,
    cursor: 'pointer',
})

export default function QaCoachButtons({ member, team }) {
    const [open, setOpen] = useState(false)
    const [mode, setMode] = useState('recommendation')
    const [loading, setLoading] = useState(false)
    const [text, setText] = useState('')
    const [error, setError] = useState('')
    const [copied, setCopied] = useState(false)

    async function run(nextMode) {
        setMode(nextMode)
        setOpen(true)
        setLoading(true)
        setError('')
        setText('')
        setCopied(false)
        try {
            const res = await fetch('/api/pulse/coach', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    mode: nextMode,
                    member: {
                        name: member.name,
                        score: member.score,
                        bugsCreated: member.bugsCreated,
                        bugsClosed: member.bugsClosed,
                        qaStoriesClosed: member.qaStoriesClosed,
                        storiesAutomated: member.storiesAutomated,
                        aiCompletedTests: member.aiCompletedTests,
                        mergeRequests: member.mergeRequests,
                        totalCommits: member.totalCommits,
                        e2eCommits: member.commitsByRepo?.e2e ?? 0,
                        badges: (member.badges ?? []).map((b) => b.badge),
                        focusMix: focusMix(member),
                    },
                    team,
                }),
            })
            const data = await res.json()
            if (!res.ok) throw new Error(data.error || 'Request failed')
            setText(data.text)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Request failed')
        } finally {
            setLoading(false)
        }
    }

    async function copy() {
        await navigator.clipboard.writeText(text)
        setCopied(true)
        window.setTimeout(() => setCopied(false), 1400)
    }

    const meta = MODE_META[mode]
    const firstName = member.name.split(' ')[0]

    return (
        <>
            <style>{'@keyframes qaCoachSpin { to { transform: rotate(360deg); } }'}</style>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                    type="button"
                    title={`Coaching recommendation for ${firstName}`}
                    onClick={() => run('recommendation')}
                    style={pillButton('#A5B4FC', 'rgba(99,102,241,0.15)', 'rgba(99,102,241,0.4)')}
                >
                    💡 Coach
                </button>
                <button
                    type="button"
                    title={`Encouragement note for ${firstName}`}
                    onClick={() => run('encouragement')}
                    style={pillButton('#F9A8D4', 'rgba(236,72,153,0.15)', 'rgba(236,72,153,0.4)')}
                >
                    💌
                </button>
            </div>

            {open && (
                <div
                    onClick={() => setOpen(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 1000,
                        background: 'rgba(0,0,0,0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '16px',
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: '100%',
                            maxWidth: '560px',
                            maxHeight: '85vh',
                            overflowY: 'auto',
                            background: PULSE_COLORS.cardBg,
                            border: `1px solid ${PULSE_COLORS.border}`,
                            borderRadius: '12px',
                            boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '16px 20px',
                                borderBottom: `1px solid ${PULSE_COLORS.border}`,
                            }}
                        >
                            <span
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    color: PULSE_COLORS.text,
                                }}
                            >
                                <span>{meta.emoji}</span>
                                {meta.title} · {member.name}
                            </span>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: PULSE_COLORS.textMuted,
                                    fontSize: '20px',
                                    lineHeight: 1,
                                    cursor: 'pointer',
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ padding: '20px' }}>
                            <p
                                style={{
                                    marginTop: 0,
                                    marginBottom: '12px',
                                    fontSize: '12px',
                                    color: PULSE_COLORS.textMuted,
                                }}
                            >
                                {meta.blurb}
                            </p>

                            {loading && (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(99,102,241,0.4)',
                                        background: 'rgba(99,102,241,0.1)',
                                        padding: '16px',
                                        fontSize: '13px',
                                        color: '#C7D2FE',
                                    }}
                                >
                                    <Spinner />
                                    <span>Reading {firstName}&apos;s activity and drafting…</span>
                                </div>
                            )}

                            {error && (
                                <div
                                    style={{
                                        borderRadius: '8px',
                                        border: '1px solid rgba(239,68,68,0.4)',
                                        background: 'rgba(239,68,68,0.1)',
                                        padding: '16px',
                                        fontSize: '13px',
                                        color: '#FCA5A5',
                                    }}
                                >
                                    <div style={{ fontWeight: 600 }}>Could not generate this insight.</div>
                                    <div style={{ marginTop: '4px' }}>{error}</div>
                                    <button
                                        type="button"
                                        onClick={() => run(mode)}
                                        style={{
                                            marginTop: '12px',
                                            borderRadius: '6px',
                                            border: '1px solid rgba(239,68,68,0.5)',
                                            background: 'transparent',
                                            color: '#FCA5A5',
                                            padding: '6px 12px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                        }}
                                    >
                                        Retry
                                    </button>
                                </div>
                            )}

                            {!loading && !error && text && (
                                <div
                                    style={{
                                        borderRadius: '8px',
                                        border: `1px solid ${PULSE_COLORS.border}`,
                                        background: PULSE_COLORS.bgInner,
                                        padding: '16px',
                                    }}
                                >
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        {renderText(text)}
                                    </div>
                                    <div
                                        style={{
                                            marginTop: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '8px',
                                        }}
                                    >
                                        <span style={{ fontSize: '11px', color: PULSE_COLORS.textSubtle }}>
                                            Generated by Claude from live Jira + GitLab activity
                                        </span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <button
                                                type="button"
                                                onClick={copy}
                                                style={{
                                                    borderRadius: '6px',
                                                    border: `1px solid ${PULSE_COLORS.border}`,
                                                    background: 'transparent',
                                                    color: PULSE_COLORS.textMuted,
                                                    padding: '4px 10px',
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {copied ? 'Copied' : 'Copy'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => run(mode)}
                                                style={{
                                                    borderRadius: '6px',
                                                    border: `1px solid ${PULSE_COLORS.border}`,
                                                    background: 'transparent',
                                                    color: PULSE_COLORS.textMuted,
                                                    padding: '4px 10px',
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                Regenerate
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
